export const AUDIENCE_BANDS = ["small", "medium", "large"];
export const AUDIENCE_BAND_RANGE = {
    small: { min: 1, max: 49 },
    medium: { min: 50, max: 100 },
    large: { min: 101, max: null },
};
export function bandLabel(band, lang = "sv") {
    const { min, max } = AUDIENCE_BAND_RANGE[band];
    const unit = lang === "sv" ? "platser" : "seats";
    return max === null ? `${min - 1}+ ${unit}` : `${min}–${max} ${unit}`;
}
export const EMPTY_PLAN_MATRIX = {
    currency: "SEK",
    vatBps: 2500,
    levels: [],
    rental: { playWindowHours: 48, prices: {} },
};
export const PLAN_LIMITS = {
    levels: 12,
    label: 60,
    films: 10000,
    price: 10000000,
    playWindowHours: 24 * 365,
};
export function planCell(matrix, band, levelId) {
    const level = matrix.levels.find((l) => l.id === levelId);
    const price = level === null || level === void 0 ? void 0 : level.prices[band];
    if (!level || price === undefined)
        return null;
    return {
        band,
        levelId,
        label: level.label,
        films: level.films,
        yearlyPrice: price,
    };
}
export function soldCells(matrix) {
    const out = [];
    for (const level of matrix.levels) {
        for (const band of AUDIENCE_BANDS) {
            const cell = planCell(matrix, band, level.id);
            if (cell)
                out.push(cell);
        }
    }
    return out;
}
export function isUpgrade(from, to) {
    const rank = (b) => AUDIENCE_BANDS.indexOf(b);
    const films = (n) => (n === null ? Infinity : n);
    const bandUp = rank(to.band) - rank(from.band);
    const filmsUp = films(to.films) - films(from.films);
    if (bandUp < 0 || filmsUp < 0)
        return false;
    return bandUp > 0 || filmsUp > 0;
}
function price(v) {
    if (v === undefined || v === null || v === "")
        return undefined;
    return typeof v === "number" &&
        Number.isFinite(v) &&
        v >= 0 &&
        v <= PLAN_LIMITS.price
        ? Math.round(v * 100) / 100
        : false;
}
function prices(raw) {
    const src = raw && typeof raw === "object" ? raw : {};
    const out = {};
    for (const band of AUDIENCE_BANDS) {
        const p = price(src[band]);
        if (p === false)
            return false;
        if (p !== undefined)
            out[band] = p;
    }
    return out;
}
export function readPlanMatrix(raw) {
    var _a, _b, _c;
    const src = raw && typeof raw === "object" ? raw : {};
    const vatBps = (_a = src.vatBps) !== null && _a !== void 0 ? _a : 2500;
    if (typeof vatBps !== "number" ||
        !Number.isInteger(vatBps) ||
        vatBps < 0 ||
        vatBps > 10000) {
        return { ok: false, problem: "vat" };
    }
    const rawLevels = Array.isArray(src.levels) ? src.levels : [];
    if (rawLevels.length > PLAN_LIMITS.levels) {
        return { ok: false, problem: "levels_too_many" };
    }
    const levels = [];
    const ids = new Set();
    const allowances = new Set();
    for (const item of rawLevels) {
        const l = item && typeof item === "object" ? item : {};
        const id = typeof l.id === "string" ? l.id : "";
        if (!/^[a-z0-9-]{1,40}$/.test(id))
            return { ok: false, problem: "level_id" };
        if (ids.has(id))
            return { ok: false, problem: "level_id_duplicate" };
        ids.add(id);
        const label = typeof l.label === "string" ? l.label.trim() : "";
        if (!label || label.length > PLAN_LIMITS.label) {
            return { ok: false, problem: "level_label" };
        }
        const films = (_b = l.films) !== null && _b !== void 0 ? _b : null;
        if (films !== null &&
            (typeof films !== "number" ||
                !Number.isInteger(films) ||
                films < 1 ||
                films > PLAN_LIMITS.films)) {
            return { ok: false, problem: "level_films" };
        }
        if (allowances.has(films)) {
            return { ok: false, problem: "level_films_duplicate" };
        }
        allowances.add(films);
        const p = prices(l.prices);
        if (p === false)
            return { ok: false, problem: "price" };
        levels.push({ id, label, films: films, prices: p });
    }
    const rawRental = src.rental && typeof src.rental === "object"
        ? src.rental
        : {};
    const hours = (_c = rawRental.playWindowHours) !== null && _c !== void 0 ? _c : 48;
    if (typeof hours !== "number" ||
        !Number.isInteger(hours) ||
        hours < 1 ||
        hours > PLAN_LIMITS.playWindowHours) {
        return { ok: false, problem: "play_window" };
    }
    const rentalPrices = prices(rawRental.prices);
    if (rentalPrices === false)
        return { ok: false, problem: "price" };
    return {
        ok: true,
        matrix: {
            currency: "SEK",
            vatBps,
            levels,
            rental: { playWindowHours: hours, prices: rentalPrices },
        },
    };
}
const DAY_MS = 24 * 60 * 60 * 1000;
export function upgradeCharge(args) {
    const yearStart = new Date(args.renewsAt);
    yearStart.setUTCFullYear(yearStart.getUTCFullYear() - 1);
    const yearMs = args.renewsAt.getTime() - yearStart.getTime();
    const leftMs = Math.min(yearMs, Math.max(0, args.renewsAt.getTime() - args.now.getTime()));
    const share = Math.ceil(leftMs / DAY_MS) / Math.round(yearMs / DAY_MS);
    const diff = Math.max(0, args.toYearly - args.fromYearly);
    return Math.round(diff * share * 100) / 100;
}
